import React, { useEffect, useCallback, useRef } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { AutoAwesomeOutlined as AutoAwesomeOutlinedIcon } from "@mui/icons-material";
import { ChatInput, colors } from "@ippon/shared-components";
import { formatTheme } from "@/theme/formatTheme";
import { useAppDispatch } from "@/store/hooks";
import { requestChatOpen, warmupAgent } from "@/store/chatSlice";

// ヘッダー(50px)を差し引いた表示領域の高さ。あいさつ+入力を縦中央に置く。
const CONTENT_HEIGHT = "calc(100vh - 80px)";

/**
 * ホーム = チャットのランチャー。
 * あいさつ + 入力だけを中央に置き、送信するとフローティングチャット(AIChatButton)を
 * 全画面(大画面)で開いて、その文言をそのまま送信する。会話ビューは Home 内に持たない。
 */
const HomePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  // 入力欄の位置を、開くアニメーションの起点として渡すための ref。
  const inputRef = useRef<HTMLDivElement>(null);

  // 入室時にエージェントをウォームアップ（初回送信の待ち時間を短縮）。
  useEffect(() => {
    void dispatch(warmupAgent());
  }, [dispatch]);

  // 送信 → 大画面チャットを、入力位置を起点に広げて開き、即送信（ランチャー）。
  const handleSendMessage = useCallback(
    (content: string) => {
      const q = content.trim();
      if (!q) return;
      const rect = inputRef.current?.getBoundingClientRect();
      const origin = rect
        ? { originX: rect.left + rect.width / 2, originY: rect.top + rect.height / 2 }
        : {};
      dispatch(requestChatOpen({ sendQuery: q, expand: true, ...origin }));
    },
    [dispatch]
  );

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        minHeight: CONTENT_HEIGHT,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        // 900px 未満は入力を全幅にするため横パディングを外す
        // （あいさつ文だけ下で余白を戻す）。
        px: { xs: 0, md: 3 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          // 900px 未満は上限なし(入力を全幅寄りに)、900px 以上は 720 で中央寄せ。
          maxWidth: { xs: "none", md: 720 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* あいさつ（入力が全幅でも文字が端に付かないよう余白を戻す） */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 3,
            px: { xs: 2, md: 0 },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, #5CC8E0 100%)`,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(11, 157, 189, 0.25)",
            }}
          >
            <AutoAwesomeOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            sx={{
              fontSize: { xs: 20, sm: 26 },
              fontWeight: 700,
              color: colors.text.primary,
              letterSpacing: "0.01em",
            }}
          >
            何かお手伝いできることはありますか？
          </Typography>
        </Box>

        {/* 中央の入力。900px 未満は共通レイアウトの横パディングを
            負マージンで一部打ち消して全幅寄りにする。 */}
        <Box
          ref={inputRef}
          sx={{
            // 親幅にきっちり伸ばし、負マージンで左右対称に外へ出す。
            alignSelf: "stretch",
            mx: { xs: -1, md: 0 },
          }}
        >
          <ChatInput
            onSendMessage={handleSendMessage}
            primaryColor={formatTheme.colors.primary.main}
            isExpanded={!isMobile}
            placeholder="どのようなお手伝いをさせていただけますか？"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HomePage;
